
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Save, Send, Phone, Bell, MessageCircle, CheckCircle, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AdminSettings } from "@/types/adminSettings";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SmsNotificationsTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const SmsNotificationsTab = ({ settings, updateSettings, saveSettings, isSaving }: SmsNotificationsTabProps) => {
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("Test SMS from LamuLamu admin");
  const [sendingTest, setSendingTest] = useState(false);
  
  // OTP Test state
  const [otpTestPhone, setOtpTestPhone] = useState("");
  const [otpTestMessage, setOtpTestMessage] = useState("Your LamuLamu verification code is: 123456");
  const [sendingOtpTest, setSendingOtpTest] = useState(false);
  
  const { toast } = useToast();

  // Initialize SMS settings if they don't exist
  const smsSettings = settings.sms || {
    partnerID: "13693",
    shortcode: "TextSMS", 
    endpoint: "regular",
    callbackUrl: "",
    adminPhone: "",
    enabled: false,
    notifications: {
      signups: true,
      bookings: true,
      cancellations: true,
      reviews: true
    },
    templates: {
      signup: "New signup: {{name}} ({{email}}) - LamuLamu",
      booking: "New booking: {{name}} for {{service}} on {{date}} at {{time}} - LamuLamu",
      cancellation: "Booking cancelled: {{name}} for {{service}} on {{date}} - LamuLamu",
      review: "New review: {{name}} rated {{service}} {{rating}}/5 - LamuLamu"
    }
  };

  const handleSave = async () => {
    await saveSettings();
  };

  const sendTestSMS = async () => {
    if (!testPhone.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a phone number to test"
      });
      return;
    }

    setSendingTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: {
          partnerID: smsSettings.partnerID,
          message: testMessage,
          shortcode: smsSettings.shortcode,
          mobile: testPhone,
          endpoint: 'otp' // Use OTP endpoint for all SMS
        }
      });

      if (error) throw error;

      toast({
        title: "Test SMS Sent",
        description: `SMS sent successfully to ${testPhone}`,
      });
    } catch (error: any) {
      console.error('Error sending test SMS:', error);
      toast({
        variant: "destructive",
        title: "Error sending SMS",
        description: error.message || "Failed to send test SMS"
      });
    } finally {
      setSendingTest(false);
    }
  };

  const sendOtpTest = async () => {
    if (!otpTestPhone.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a phone number for OTP test"
      });
      return;
    }

    setSendingOtpTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: {
          partnerID: smsSettings.partnerID,
          message: otpTestMessage,
          shortcode: smsSettings.shortcode,
          mobile: otpTestPhone,
          endpoint: 'otp' // Explicitly use OTP endpoint
        }
      });

      if (error) throw error;

      toast({
        title: "OTP Test SMS Sent",
        description: `OTP SMS sent successfully to ${otpTestPhone}`,
      });
    } catch (error: any) {
      console.error('Error sending OTP test SMS:', error);
      toast({
        variant: "destructive",
        title: "Error sending OTP SMS",
        description: error.message || "Failed to send OTP test SMS"
      });
    } finally {
      setSendingOtpTest(false);
    }
  };

  const getTemplateVariables = (type: string) => {
    const common = ["{{name}}", "{{email}}"];
    switch (type) {
      case 'booking':
        return [...common, "{{service}}", "{{date}}", "{{time}}"];
      case 'cancellation':
        return [...common, "{{service}}", "{{date}}"];
      case 'review':
        return [...common, "{{service}}", "{{rating}}"];
      default:
        return common;
    }
  };

  // Check configuration status
  const isConfigured = smsSettings.partnerID && smsSettings.shortcode;
  const isFullyConfigured = isConfigured && smsSettings.adminPhone && smsSettings.enabled;

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            SMS Notification Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {isFullyConfigured ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                <Badge variant="default" className="bg-green-100 text-green-800">
                  Fully Configured
                </Badge>
                <span className="text-sm text-muted-foreground">
                  SMS notifications are enabled and ready to send
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  {isConfigured ? "Partially Configured" : "Not Configured"}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {!isConfigured ? "API settings required" : "Enable SMS and set admin phone"}
                </span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="notifications" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                SMS Notification Settings
              </CardTitle>
              <CardDescription>
                Configure which events trigger SMS notifications to the admin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="sms-enabled">Enable SMS Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Master switch for all SMS notifications
                    </p>
                  </div>
                  <Switch
                    id="sms-enabled"
                    checked={smsSettings.enabled}
                    onCheckedChange={(checked) => updateSettings(['sms', 'enabled'], checked)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin-phone">Admin Phone Number</Label>
                  <Input
                    id="admin-phone"
                    value={smsSettings.adminPhone || ''}
                    onChange={(e) => updateSettings(['sms', 'adminPhone'], e.target.value)}
                    placeholder="254700000000"
                    disabled={!smsSettings.enabled}
                  />
                  <p className="text-sm text-muted-foreground">
                    Phone number to receive SMS notifications (include country code)
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium">Event Notifications</h4>
                
                {[
                  { key: 'signups', label: 'New User Signups', description: 'When new users register' },
                  { key: 'bookings', label: 'New Bookings', description: 'When customers make bookings' },
                  { key: 'cancellations', label: 'Booking Cancellations', description: 'When bookings are cancelled' },
                  { key: 'reviews', label: 'New Reviews', description: 'When customers leave reviews' }
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>{item.label}</Label>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    <Switch
                      checked={smsSettings.notifications[item.key as keyof typeof smsSettings.notifications]}
                      onCheckedChange={(checked) => 
                        updateSettings(['sms', 'notifications', item.key], checked)
                      }
                      disabled={!smsSettings.enabled}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                SMS Templates
              </CardTitle>
              <CardDescription>
                Customize the SMS messages sent for different events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { key: 'signup', label: 'New Signup', description: 'Message sent when new users register' },
                { key: 'booking', label: 'New Booking', description: 'Message sent when customers make bookings' },
                { key: 'cancellation', label: 'Booking Cancellation', description: 'Message sent when bookings are cancelled' },
                { key: 'review', label: 'New Review', description: 'Message sent when customers leave reviews' }
              ].map((template) => (
                <div key={template.key} className="space-y-3">
                  <div>
                    <Label htmlFor={`template-${template.key}`}>{template.label} Template</Label>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                  </div>
                  <Textarea
                    id={`template-${template.key}`}
                    value={smsSettings.templates[template.key as keyof typeof smsSettings.templates]}
                    onChange={(e) => updateSettings(['sms', 'templates', template.key], e.target.value)}
                    placeholder={`Enter ${template.label.toLowerCase()} template...`}
                    className="min-h-[80px]"
                  />
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Available variables:</span> {' '}
                    {getTemplateVariables(template.key).join(', ')}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="configuration" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                SMS API Configuration
              </CardTitle>
              <CardDescription>
                Configure SMS settings for TextSMS.co.ke API integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="partnerID">Partner ID</Label>
                  <Input
                    id="partnerID"
                    value={smsSettings.partnerID}
                    onChange={(e) => updateSettings(['sms', 'partnerID'], e.target.value)}
                    placeholder="13693"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shortcode">Shortcode</Label>
                  <Input
                    id="shortcode"
                    value={smsSettings.shortcode}
                    onChange={(e) => updateSettings(['sms', 'shortcode'], e.target.value)}
                    placeholder="TextSMS"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endpoint">Default Endpoint</Label>
                  <Select
                    value={smsSettings.endpoint}
                    onValueChange={(value) => updateSettings(['sms', 'endpoint'], value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select endpoint" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular SMS</SelectItem>
                      <SelectItem value="otp">OTP Endpoint</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="callbackUrl">Callback URL</Label>
                  <Input
                    id="callbackUrl"
                    value={smsSettings.callbackUrl}
                    onChange={(e) => updateSettings(['sms', 'callbackUrl'], e.target.value)}
                    placeholder={`${window.location.origin}/api/sms-callback`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Test SMS
              </CardTitle>
              <CardDescription>
                Send a test SMS to verify your configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="test-phone">Test Phone Number</Label>
                  <Input
                    id="test-phone"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="254700000000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="test-message">Test Message</Label>
                  <Input
                    id="test-message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Test message"
                  />
                </div>
              </div>
              <Button 
                onClick={sendTestSMS} 
                disabled={sendingTest || !testPhone.trim()}
                className="flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                {sendingTest ? "Sending..." : "Send Test SMS"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                OTP Test
              </CardTitle>
              <CardDescription>
                Send a test OTP SMS using the dedicated OTP endpoint for sensitive transactions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="otp-test-phone">Test Phone Number</Label>
                  <Input
                    id="otp-test-phone"
                    value={otpTestPhone}
                    onChange={(e) => setOtpTestPhone(e.target.value)}
                    placeholder="254700000000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp-test-message">OTP Message</Label>
                  <Input
                    id="otp-test-message"
                    value={otpTestMessage}
                    onChange={(e) => setOtpTestMessage(e.target.value)}
                    placeholder="Your verification code is: 123456"
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground p-3 bg-blue-50 rounded-md border border-blue-200">
                <strong>OTP Endpoint:</strong> This test uses the dedicated OTP endpoint (https://sms.textsms.co.ke/api/services/sendotp/) 
                specifically designed for sensitive transaction traffic like verification codes.
              </div>
              <Button 
                onClick={sendOtpTest} 
                disabled={sendingOtpTest || !otpTestPhone.trim()}
                className="flex items-center gap-2"
              >
                <Phone className="h-4 w-4" />
                {sendingOtpTest ? "Sending..." : "Send OTP Test"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end pt-4 border-t">
        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSaving ? "Saving..." : "Save SMS Settings"}
        </Button>
      </div>
    </div>
  );
};
